import uiConfig from 'src/lib/ui/config';
import { animate, ImageCoverView, View } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import * as Fate from 'src/replicant/fate';
import { createEmitter } from 'src/lib/Emitter';
import {
  getLaunchScene,
  isSceneEntered,
  isSceneEntering,
} from 'src/lib/stateUtils';
import i18n from 'src/lib/i18n/i18n';
import { Transition } from 'src/game/logic/SceneManager';
import { GCInstant as platform } from 'src/lib/gcinstant';
import StateObserver, { user } from 'src/StateObserver';
import { getPet } from '../pet';
import {
  createWaiterInView,
  getFooter,
  getHeader,
  getSideButtonsY,
} from 'src/lib/utils';
import { petIdleSequence } from '../pet/pet.sequence';
import { AssetGroup } from '@play-co/timestep-core';
import { HomeButtonsAnimated } from '../components/HomeButtonsAnimated';

export default class BedroomScene extends ResponsiveScene {
  private buttons: HomeButtonsAnimated;

  public getIcon(icon: string): View | undefined {
    return this.buttons.getButton(icon);
  }

  public getHomeButtons() {
    return this.buttons;
  }

  constructor() {
    super(
      new ImageCoverView({
        ...uiConfig.buruburu.backgroundDefaults,
        ...uiConfig.buruburu.bedroom,
      }),
    );

    this.getView().tag = 'bedroom';

    this.addBed();
    this.addDimmingOverlay();
    this.addSleepingSign();

    this.buttons = new HomeButtonsAnimated({
      scene: 'bedroom',
      superview: this.getView(),
      width: this.getView().style.width,
      height: 900,
      y: 180,
      canHandleEvents: false,
      zIndex: uiConfig.buruburu.bedroomOverlay.zIndex + 1,
    });
  }

  public async preload() {
    await new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/bedroom/']),
    ).load();
  }

  private addBed() {
    const scene = this.getView();
    scene.addSubview(new ImageCoverView(uiConfig.buruburu.bedroomBed));
  }

  private addDimmingOverlay() {
    const scene = this.getView();
    const overlay = scene.addSubview(
      new View(uiConfig.buruburu.bedroomOverlay),
    );

    let skipDimTransition = getLaunchScene() === 'bedroom';
    createEmitter(scene, () => isSceneEntering('bedroom')).addListener(
      (isEnteringBedroom) => {
        if (isEnteringBedroom) {
          const { width, height } = scene.style;
          const opacity = skipDimTransition ? 1 : 0;
          overlay.updateOpts({ opacity, width, height });
        }
      },
    );
    createEmitter(
      scene,
      ({ pet }) => isSceneEntered('bedroom') && !pet[platform.playerID].action,
    ).addListener((shouldDimLights) => {
      if (shouldDimLights) {
        animate(overlay).now({ opacity: 1 }, 3000, animate.linear);
        skipDimTransition = false; // Only skip the dim transition when launching into the bedroom
      }
    });
    createEmitter(scene, ({ ui }) => ui.screenSize).addListener((screen) => {
      overlay.updateOpts({
        width: screen.width,
        height: screen.height,
      });
    });
  }

  private addSleepingSign() {
    const scene = this.getView();
    const { bedroomSign, bedroomSignText } = uiConfig.buruburu;
    const { startHour, endHour } = Fate.ruleset.sleepingPeriod;
    const sign = scene.addSubview(new ImageCoverView(bedroomSign));
    sign.addSubview(
      new LangBitmapFontTextView({
        ...bedroomSignText,
        localeText: () =>
          i18n('sleepingPeriod', {
            startHour: startHour - 12,
            endHour,
          }),
      }),
    );
  }

  public transition(): Transition {
    return 'slide_forward';
  }

  public showScene() {
    this.buttons.showScene();
    this.setSideButtonPositions();
    const timeUntilAwake = Fate.getters.getTimeUntilAwake(
      StateObserver.getState().user,
      StateObserver.now(),
    );
    createWaiterInView(this.getView()).wait(() => {
      getPet()
        .moveTo({ scene: 'livingRoom', location: 'c' })
        .then(petIdleSequence);
    }, timeUntilAwake);
  }

  public hideScene(): void {
    this.buttons.hideScene();
  }

  private setSideButtonPositions() {
    const y = getSideButtonsY(this.getView());
    let height = this.getView().style.height - y;
    height -= getFooter()!.style.height;
    height -= 20; // Small marging

    this.buttons.updateOpts({ y, height });
  }

  public pauseScene() {
    // This will pause buttons updates
    this.buttons.hideScene();
  }

  public resumeScene(): void {
    // This will resume buttons updates
    this.buttons.showScene();
  }
}
